import { Link } from 'react-router-dom';
import VerticalComparisonMatrix from '../../components/VerticalComparisonMatrix';

function Section({ title, bullets }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-[var(--gradient-card)] p-7 backdrop-blur-xl">
      <div className="text-lg font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
        {title}
      </div>
      <ul className="mt-4 grid gap-2 text-sm text-[var(--text-secondary)]">
        {bullets.map((b) => (
          <li key={b} className="leading-6">
            {b}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function DigitalNatives() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[var(--gradient-hero)] p-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(6,182,212,0.12),transparent_55%),radial-gradient(circle_at_75%_25%,rgba(37,99,235,0.18),transparent_45%)]" />
        <div className="relative">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Industries</div>
          <h1
            className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Digital Natives
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-[var(--text-secondary)] sm:text-base">
            Digital-native organizations including e-commerce, fintech, SaaS, gaming, and platform companies require
            identity solutions that scale rapidly, support modern development practices, enable frictionless Customer
            experiences, and integrate seamlessly with cloud-native architectures.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-xl bg-[var(--accent-trust)] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/20"
            >
              Request Digital Natives Assessment
            </Link>
            <Link
              to="/capabilities"
              className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.02] px-5 py-3 text-sm font-semibold text-[var(--text-primary)] hover:bg-white/[0.05]"
            >
              Explore Core Capabilities
            </Link>
          </div>
        </div>
      </div>

      <div className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          Customer Identity & Experience
        </h2>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Section
            title="Social & Passwordless Login"
            bullets={[
              "One-click authentication via Google, Facebook, Apple with fallback to magic links and passkeys",
            ]}
          />
          <Section
            title="Progressive Profiling"
            bullets={[
              "Minimal friction onboarding with gradual identity collection based on user journey and risk level",
            ]}
          />
          <Section
            title="Cross-Platform Identity"
            bullets={["Unified user identity across web, mobile apps, smart TV, and IoT devices"]}
          />
          <Section
            title="Guest to Registered Conversion"
            bullets={[
              "Seamless transition from guest checkout to registered accounts with cart/preference preservation",
            ]}
          />
          <Section
            title="Subscription Identity"
            bullets={[
              "Multi-user household accounts with profile management, parental controls, and usage tracking",
            ]}
          />
          <Section
            title="Gaming Identity"
            bullets={[
              "Cross-game identity with achievement portability, anti-cheat integration, and esports verification",
            ]}
          />
        </div>
      </div>

      <div className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          Developer & Platform Identity
        </h2>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Section
            title="API Key Management"
            bullets={[
              "Self-service API key generation with scoping, rate limiting, and usage analytics for platform developers",
            ]}
          />
          <Section
            title="OAuth 2.0/OIDC Provider"
            bullets={[
              "Acting as identity provider for third-party apps in marketplace/platform ecosystems",
            ]}
          />
          <Section
            title="Webhook Authentication"
            bullets={["Secure webhook delivery with signature verification and replay protection"]}
          />
          <Section
            title="SDK Authentication"
            bullets={["Embedded authentication for Partner integrations and white-label solutions"]}
          />
          <Section
            title="Marketplace Seller Identity"
            bullets={["Seller verification, trust scoring, and tiered access based on performance metrics"]}
          />
        </div>
      </div>

      <div className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          Modern Workforce Identity
        </h2>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Section
            title="Cloud-Native SSO"
            bullets={[
              "SAML/OIDC federation across SaaS applications (Slack, Jira, GitHub, GSuite, AWS Console)",
            ]}
          />
          <Section
            title="JIT Provisioning"
            bullets={["Just-in-time user provisioning and SCIM-based lifecycle management across cloud applications"]}
          />
          <Section
            title="Remote-First Identity"
            bullets={["Zero-trust access for distributed workforce with device trust and continuous verification"]}
          />
          <Section
            title="Gig Worker Identity"
            bullets={["Flexible identity for freelancers, contractors, and gig economy workers with project-based access"]}
          />
          <Section
            title="DevOps Identity"
            bullets={["Machine identity for CI/CD pipelines, service accounts, and infrastructure-as-code deployments"]}
          />
        </div>
      </div>

      <div className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          Cloud & Infrastructure Identity
        </h2>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Section
            title="Multi-Cloud IAM"
            bullets={["Unified identity across AWS, Azure, GCP with consistent policies and cross-cloud access"]}
          />
          <Section
            title="Kubernetes RBAC"
            bullets={["Service mesh identity with mTLS, workload identity, and namespace-level access controls"]}
          />
          <Section
            title="Secrets Management"
            bullets={[
              "Dynamic secrets, automatic rotation, and just-in-time database credentials (Vault, AWS Secrets Manager)",
            ]}
          />
          <Section
            title="Serverless Identity"
            bullets={["Function-level IAM for Lambda, Cloud Functions with least-privilege enforcement"]}
          />
          <Section
            title="Container Registry Access"
            bullets={["Image pull secrets and registry authentication for secure container deployments"]}
          />
        </div>
      </div>

      <div className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          Fraud Prevention & Trust
        </h2>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Section
            title="Bot Detection"
            bullets={["CAPTCHA alternatives and behavioral analysis to distinguish humans from automated attacks"]}
          />
          <Section
            title="Account Takeover Prevention"
            bullets={["Real-time detection of credential stuffing, session hijacking, and suspicious login patterns"]}
          />
          <Section
            title="Promo Abuse Prevention"
            bullets={["Identity linking to detect multi-account abuse of promotional offers and referral programs"]}
          />
          <Section
            title="Trust & Safety Signals"
            bullets={["User reputation scoring based on behavior, transaction history, and community feedback"]}
          />
          <Section
            title="Synthetic Identity Detection"
            bullets={["ML-based detection of fake accounts created with fabricated or stolen identity elements"]}
          />
        </div>
      </div>

      <div className="mt-14 rounded-3xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur-xl sm:p-10">
        <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          Privacy & Compliance
        </h2>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Section
            title="Consent Management"
            bullets={["Granular consent collection and preference management for DPDP, GDPR, and CCPA compliance"]}
          />
          <Section
            title="Data Subject Rights"
            bullets={["Self-service portals for access requests, data export, and account deletion (right to be forgotten)"]}
          />
          <Section
            title="Age Verification"
            bullets={["Age gating for regulated content with parental consent workflows for minors"]}
          />
          <Section
            title="Cross-Border Data"
            bullets={["Identity data residency controls and transfer mechanisms for international operations"]}
          />
          <Section
            title="SOC 2 & ISO 27001"
            bullets={["Identity controls supporting security certifications required by enterprise Customers"]}
          />
        </div>
      </div>

      <VerticalComparisonMatrix />
    </div>
  );
}
